import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";
import { appendTzToQueryKey } from "src/queries/timeWindowQueryKey";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { type FetchParams, getCleanBodyParams } from "src/utils/dfetch.ts";
import { queryFnWrapper } from "src/utils/queryFnWrapper";
import type { PartialBy } from "src/utils/ts-utils";

export type UseDQueryProps<T, Select> = FetchParams &
  PartialBy<UseQueryOptions<T, Error, Select, QueryKey>, "queryKey" | "queryFn">;

export function useDQuery<T, Select = T>(props: UseDQueryProps<T, Select>) {
  const { body, url, headers = {}, caller, ...restProps } = props;
  const tz = useGlobalLocalContext((s) => s.tz);

  const cleanBody = getCleanBodyParams(body);
  const paramsKey = JSON.stringify(cleanBody);
  const queryKey =
    props.queryKey || appendTzToQueryKey([url, paramsKey], cleanBody, tz);

  if (caller) {
    headers["X-Definity-Caller"] = caller;
  }

  const queryFn = async ({ signal }: { signal: AbortSignal }): Promise<T> => {
    const result = await queryFnWrapper<T>({ url, body, headers, signal });
    return result as unknown as T;
  };

  return useQuery<T, Error, Select, QueryKey>({
    ...restProps,
    queryKey,
    queryFn,
  });
}
